import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { AlertTriangle, Trash2, Loader2, ArrowLeft, CheckCircle, Shield } from 'lucide-react';
import { API_BASE_URL as API } from '../config/api';



export default function AdminContentWipePage() {
  const { user, isAuthenticated, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [confirmation, setConfirmation] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const isOwner = user?.role === 'owner' || user?.is_owner;
  const confirmationRequired = 'WIPE_ALL_CONTENT';

  const handleWipe = async () => {
    if (confirmation !== confirmationRequired) {
      toast.error(`Please type "${confirmationRequired}" to confirm`);
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const res = await fetch(`${API}/api/admin/content/wipe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ confirm: confirmation })
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setResult(data.results);
        toast.success('Content wipe completed successfully');
        setConfirmation('');
      } else {
        toast.error(data.detail || 'Failed to wipe content');
      }
    } catch (err) {
      console.error('Content wipe error:', err);
      toast.error('Network error - please try again');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="p-6 text-center max-w-md">
          <Shield className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h1 className="text-xl font-bold mb-2">Admin Access Required</h1>
          <p className="text-muted-foreground mb-4">Please log in as platform owner</p>
          <Button onClick={() => navigate('/login')}>Log In</Button>
        </Card>
      </div>
    );
  }

  if (!isOwner) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="p-6 text-center max-w-md">
          <Shield className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <h1 className="text-xl font-bold mb-2">Access Denied</h1>
          <p className="text-muted-foreground">Only platform owners can access this page</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 py-8">
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-bold text-red-500">⚠️ Content Reset</h1>
        </div>

        <Card className="p-6 border-red-500/30 bg-red-500/5">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-500" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-red-400">Full Content Wipe</h2>
              <p className="text-sm text-muted-foreground">This action cannot be undone</p>
            </div>
          </div>

          <div className="space-y-4 mb-6">
            <div className="bg-black/30 rounded-lg p-4 text-sm">
              <p className="font-medium text-red-400 mb-2">This will DELETE:</p>
              <ul className="space-y-1 text-muted-foreground">
                <li>• All audio tracks</li>
                <li>• All videos</li>
                <li>• All reels</li>
                <li>• All stories</li>
                <li>• All content comments</li>
                <li>• All likes and play history</li>
                <li>• All chart data</li>
                <li>• All local media files</li>
              </ul>
            </div>

            <div className="bg-green-500/10 rounded-lg p-4 text-sm">
              <p className="font-medium text-green-400 mb-2">This will PRESERVE:</p>
              <ul className="space-y-1 text-muted-foreground">
                <li>• User accounts</li>
                <li>• User profiles and handles</li>
                <li>• Artist profiles</li>
                <li>• Followers and following</li>
                <li>• Wallet balances</li>
                <li>• Subscription tiers</li>
              </ul>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <p className="text-sm mb-2">
                Type <code className="bg-red-500/20 px-2 py-0.5 rounded text-red-400">{confirmationRequired}</code> to confirm:
              </p>
              <Input
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                placeholder="Type confirmation here"
                className="font-mono"
                disabled={loading}
              />
            </div>

            <Button
              onClick={handleWipe}
              disabled={loading || confirmation !== confirmationRequired}
              variant="destructive"
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Wiping Content...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Wipe All Content
                </>
              )}
            </Button>
          </div>

          {result && (
            <div className="mt-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
              <p className="font-medium text-green-400 flex items-center gap-2 mb-3">
                <CheckCircle className="w-5 h-5" />
                Content Wipe Complete
              </p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>Tracks deleted:</div>
                <div className="text-right">{result.tracks_deleted}</div>
                <div>Videos deleted:</div>
                <div className="text-right">{result.videos_deleted}</div>
                <div>Clips deleted:</div>
                <div className="text-right">{result.reels_deleted}</div>
                <div>Stories deleted:</div>
                <div className="text-right">{result.stories_deleted}</div>
                <div>Comments deleted:</div>
                <div className="text-right">{result.comments_deleted}</div>
                <div>Files deleted:</div>
                <div className="text-right">{result.files_deleted || 0}</div>
                <div>Artists reset:</div>
                <div className="text-right">{result.artists_reset}</div>
              </div>
            </div>
          )}
        </Card>

        <p className="text-xs text-muted-foreground text-center mt-4">
          After wipe, new uploads will automatically populate charts and discover feeds.
        </p>
      </div>
    </div>
  );
}
